import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useFeatureFlags } from '../../hooks/useFeatureFlags.js';
import LanguageSwitcher from './LanguageSwitcher.jsx';
import api from '../../api/client';

// Grouped, hierarchical top menu (redesigned per explicit design feedback:
// the previous navbar was a flat list of 6-8 links that never adapted for
// mobile). Each of these renders as a single dropdown trigger instead of
// several individual top-level links, so the always-visible bar stays short
// regardless of how many routes the site accumulates — a new print-purchase
// or advertising page later just adds one more entry to the matching group
// instead of growing the bar itself.
//
// Two links are deliberately NOT in this dropdown, per explicit design
// feedback for the live-launch release:
//   - "Order Print Edition" is a top-level commerce action (it starts a
//     purchase), not a reading destination, so it now renders as its own
//     standalone top-level link instead of being buried in this menu.
//   - "Flipbook" was removed entirely as a menu entry — the Digital
//     Edition/Flipbook view is now opened via a dedicated button on the
//     Home page (see Home.jsx), directly under the "forward the stories"
//     banner, since that's the more natural place a reader discovers it.
// Feature Flags / Visibility Toggles: each entry below carries an optional
// `flag` key naming which Settings.featureFlags switch controls it — see
// hooks/useFeatureFlags.js. Entries with no `flag` (Monthly Magazine) are
// never gated; they're the core magazine-reading experience, not one of the
// four monetization toggles.
const MAGAZINE_LINKS = [
  { to: '/magazine', label: 'Monthly Magazine' },
  { to: '/magazine/archive', label: 'Magazine Archive', flag: 'printMagazineArchiveEnabled' },
];
const ADVERTISE_LINKS = [
  { to: '/marketplace', label: 'Advertise With Us', flag: 'onlineAdBookingEnabled' },
  { to: '/print-ads/order', label: 'Advertise in Print', flag: 'printAdBookingEnabled' },
];

/**
 * A single grouped menu item: a button that opens a small panel of related
 * links. Click-to-toggle (not hover) so the exact same component works for
 * both the desktop dropdown and the mobile accordion below — hover has no
 * equivalent on touch devices. Closes on an outside click/tap and whenever
 * a link inside it is followed.
 */
function NavDropdown({ label, links, onNavigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleOutsideClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  return (
    <div className="navbar-dropdown" ref={ref}>
      <button
        type="button"
        className="navbar-dropdown-toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        <span className="navbar-dropdown-caret" aria-hidden="true">
          {open ? '▴' : '▾'}
        </span>
      </button>
      {open && (
        <div className="navbar-dropdown-menu" role="menu">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onNavigate?.();
              }}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const featureFlags = useFeatureFlags();

  // Feature Flags / Visibility Toggles: filter out any link whose flag is
  // currently off. See middleware/featureFlag.js (server) for why hiding
  // the link alone isn't the actual security boundary — this is purely so a
  // disabled section doesn't show up as a dead/404ing link in the nav.
  const visibleMagazineLinks = MAGAZINE_LINKS.filter((link) => !link.flag || featureFlags[link.flag]);
  const visibleAdvertiseLinks = ADVERTISE_LINKS.filter((link) => !link.flag || featureFlags[link.flag]);

  // Every route change closes the mobile menu — otherwise it would stay
  // open (covering the page) after following a link into it.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* ignore */
    }
    logout();
    navigate('/');
  };

  const closeMobileMenu = () => setMobileOpen(false);

  return (
    <nav className="navbar">
      <div className="navbar-inner">
        <Link to="/" className="navbar-brand" onClick={closeMobileMenu}>
          The USA Rules Magazine
        </Link>

        {/* Hidden on desktop (see the max-width media query in global.css);
            on narrow viewports this is what reveals navbar-links below. */}
        <button
          type="button"
          className="navbar-burger"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((o) => !o)}
        >
          <span />
          <span />
          <span />
        </button>

        <div className={`navbar-links${mobileOpen ? ' navbar-links-open' : ''}`}>
          <Link to="/about" onClick={closeMobileMenu}>
            About Us
          </Link>
          {/* Settings.featureFlags.magazineEnabled hides this whole menu (Monthly
              Magazine + Archive) — supersedes the "Monthly Magazine is never
              gated" note on MAGAZINE_LINKS above. */}
          {featureFlags.magazineEnabled && visibleMagazineLinks.length > 0 && (
            <NavDropdown label="Magazine" links={visibleMagazineLinks} onNavigate={closeMobileMenu} />
          )}
          {featureFlags.printMagazineStoreEnabled && (
            <Link to="/magazine/order" onClick={closeMobileMenu}>
              Order Print Edition
            </Link>
          )}
          {visibleAdvertiseLinks.length > 0 && (
            <NavDropdown label="Advertise" links={visibleAdvertiseLinks} onNavigate={closeMobileMenu} />
          )}
          <LanguageSwitcher />
          {user?.role === 'admin' && (
            <Link to="/admin" onClick={closeMobileMenu}>
              Admin
            </Link>
          )}
          {user ? (
            <>
              <Link to="/dashboard" onClick={closeMobileMenu}>
                Dashboard
              </Link>
              <button
                className="btn btn-outline"
                style={{ color: '#fff' }}
                aria-label="Log out of your account"
                onClick={handleLogout}
              >
                Log out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={closeMobileMenu}>
                Log in
              </Link>
              <Link to="/signup" className="btn btn-primary" onClick={closeMobileMenu}>
                Sign up
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

